import { useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import {
  AButton,
  AModal,
  ARate,
  ASelect,
  ATextArea,
  ItemStatus,
  ToSignature,
} from '../../../../components';
import { IProfessional, ITreatmentItem } from '../../../../shared/interfaces';
import { ContainerTitle } from './styles';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/toast';

interface IEditEvolutionProps {
  open: boolean;
  close: () => void;
  professionals: IProfessional[];
  type: number | undefined;
  treatmentItem: ITreatmentItem | null;
  updateProfessional: boolean;
  setTreatmentItems: React.Dispatch<React.SetStateAction<ITreatmentItem[]>>;
}

const EditEvolution: React.FC<IEditEvolutionProps> = ({
  open,
  close,
  type,
  professionals,
  treatmentItem,
  updateProfessional,
  setTreatmentItems,
}) => {
  const { addToast } = useToast();
  const [observation, setObservation] = useState<string>();
  const [rank, setRank] = useState<number>(0);
  const [professionalId, setProfessionalId] = useState<string | undefined>();
  const [modalSign, setModalSign] = useState<boolean>(false);
  const [signaturePassword, setSignaturePassword] = useState<string | null>(
    null,
  );

  const handleSubmit = useCallback(async () => {
    if (treatmentItem) {
      if (updateProfessional) {
        await api
          .patch(`/treatments-items/${treatmentItem.id}`, {
            professional_id: professionalId,
            update_professional: updateProfessional,
          })
          .then((response) => {
            setTreatmentItems((state) =>
              state.map((e) => (e.id !== response.data.id ? e : response.data)),
            );
            addToast({
              type: 'info',
              title: 'Alterado',
              description: 'Dados alterados com sucesso.',
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: error.response?.data?.message,
            });
          });
        return;
      }

      await api
        .patch(`/treatments-items/${treatmentItem.id}`, {
          treatment_id: treatmentItem.treatment_id,
          procedure_id: treatmentItem.procedure_id,
          professional_id: treatmentItem.professional_id,
          observation,
          status: type,
          rank,
          update_professional: updateProfessional,
        })
        .then(async (response) => {
          if (signaturePassword && response.data?.last_log_id) {
            await api.post('/signature-validation/sign', {
              document_type: 'TREATMENT_EVOLUTION',
              record_id: response.data.last_log_id,
              professional_id: treatmentItem.professional_id,
              password: signaturePassword,
            });
          }

          setTreatmentItems((state) =>
            state.map((e) => (e.id !== response.data.id ? e : response.data)),
          );
          addToast({
            type: 'info',
            title: 'Alterado',
            description: signaturePassword
              ? 'Evolução atualizada e assinada com sucesso.'
              : 'Dados alterados com sucesso.',
          });
          setSignaturePassword(null);
          close();
        })
        .catch((error) => {
          addToast({
            type: 'error',
            title: 'Erro',
            description: error.response.data.message,
          });
        });
    } else {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Item nâo encontrado',
      });
    }
  }, [
    observation,
    professionalId,
    rank,
    treatmentItem,
    updateProfessional,
    type,
    setTreatmentItems,
    addToast,
    close,
  ]);

  const renderFields = () => {
    if (!updateProfessional) {
      return (
        <>
          <Form.Item label="Observação">
            <ATextArea
              rows={3}
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              showCount
              maxLength={250}
              style={{ resize: 'none' }}
            />
          </Form.Item>
          <Form.Item label="Avaliação da Evolução">
            <ARate
              value={rank}
              onChange={(value) => setRank(value)}
              tooltips={['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']}
              count={10}
            />
          </Form.Item>
          {treatmentItem?.professional_id && (
            <Form.Item label="Assinatura Digital">
              <AButton type="dashed" onClick={() => setModalSign(true)}>
                {signaturePassword
                  ? 'Assinatura validada para salvar'
                  : 'Autenticar assinatura do profissional'}
              </AButton>
            </Form.Item>
          )}
        </>
      );
    }
    return (
      <Form.Item label="Profissional" required>
        <ASelect
          showSearch
          placeholder="Profissional"
          value={professionalId}
          onChange={(e) => setProfessionalId(e)}
          filterOption={(input, option: any) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          allowClear
          options={professionals.map((prof) => ({
            label: prof.name,
            value: prof.id,
          }))}
        />
      </Form.Item>
    );
  };

  useEffect(() => {
    if (open) {
      setObservation(treatmentItem?.observation || '');
      setRank(treatmentItem?.rank || 0);
      if (updateProfessional) {
        setProfessionalId(treatmentItem?.professional_id);
      }
    }
  }, [open, treatmentItem, updateProfessional]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        updateProfessional ? (
          'Atribuir procedimento para'
        ) : (
          <ContainerTitle>
            Atualizar status para <ItemStatus type={type} marginLeft />
          </ContainerTitle>
        )
      }
      okText={'Atualizar'}
      onOk={() => handleSubmit()}
    >
      <>
        <Form layout="vertical">{renderFields()}</Form>
        <ToSignature
          open={modalSign}
          close={() => setModalSign(false)}
          professional_id={treatmentItem?.professional_id}
          onAuthenticated={async (_authData, password) => {
            setSignaturePassword(password);
            addToast({
              type: 'success',
              title: 'Assinatura validada',
              description: 'A evolução será assinada ao atualizar.',
            });
          }}
        />
      </>
    </AModal>
  );
};

export default EditEvolution;
